import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppHeader, AppShell, Button, ShellPanel } from '@origo/origo-ui';
import { Map, useMapViewContext } from '@origo/map-context';
import Point from '@arcgis/core/geometry/Point.js';
import { useIsNarrow } from '../hooks/useIsNarrow';
import { useAppSelector } from '../store/storeHooks';
import { selectUser } from '@origo/auth';
import geodataLogo from '../assets/geodata-logo.svg';
import { VersionTrigger, ChangelogDialog, NewVersionNotice, useNewVersionNotice } from './VersionInfo';
import {
    AddressSearch,
    useAddressSearch,
    SearchSuggestionLayer,
    type AddressSuggestion,
} from '@origo/origo-components';
import changelogMarkdown from '../../CHANGELOG.md?raw';
import PlanregisterView from '../features/planregister/PlanregisterView';
import { useUnsavedChangesGuard } from '../features/planregister/hooks/useUnsavedChangesGuard';
import styles from './AppLayout.module.css';

interface AppLayoutProps {
    onSignOut?: () => void;
    /** ArcGIS WebMap-id som vises i kart-kolonnen. */
    webMapId?: string;
}

function getInitials(name: string): string {
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

/**
 * Header-breakpoints. AppHeader har ingen egne media queries, og de interne
 * klassene er CSS Modules-hashet, så elementene må droppes i JS framfor å
 * skjules med app-lokal CSS. Rekkefølgen de faller ut i:
 *   1100px — undertittel (med skillelinje)
 *    960px — adressesøket, samtidig som .mapArea skjules (AppLayout.module.css);
 *            søket har ingen nytte uten kart å zoome i
 *    720px — brukerchippen, så logo + tittel + handlinger får plass
 */
const HEADER_SUBTITLE_BREAKPOINT = 1100;
const HEADER_SEARCH_BREAKPOINT = 960;
const HEADER_USER_BREAKPOINT = 720;

const PANEL_DEFAULT_WIDTH_VW = 66.667;
const PANEL_MIN_WIDTH = 520;
const PANEL_MAX_WIDTH_VW = 85;

/** Startbredde i px, så ShellPanel ikke animerer ut fra 320px ved første render. */
function getInitialPanelWidth(): number {
    const vwToPx = (vw: number) => Math.floor(window.innerWidth * vw / 100);
    return Math.min(vwToPx(PANEL_MAX_WIDTH_VW), Math.max(PANEL_MIN_WIDTH, vwToPx(PANEL_DEFAULT_WIDTH_VW)));
}

const AppLayout: React.FC<AppLayoutProps> = ({ onSignOut, webMapId }) => {
    const { t } = useTranslation();
    const hideSubtitle = useIsNarrow(HEADER_SUBTITLE_BREAKPOINT);
    const hideSearch = useIsNarrow(HEADER_SEARCH_BREAKPOINT);
    const hideUser = useIsNarrow(HEADER_USER_BREAKPOINT);
    const user = useAppSelector(selectUser);
    const { mapView } = useMapViewContext();
    const [initialPanelWidth] = useState(getInitialPanelWidth);
    const [changelogOpen, setChangelogOpen] = useState(false);
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const { hasNewVersion, dismiss } = useNewVersionNotice();
    // Utlogging sletter innloggingen før siden lastes på nytt, så
    // `beforeunload` kommer for sent; den må gå via vakten først.
    const unsaved = useUnsavedChangesGuard();

    // Adresse-/gnr-bnr-søk i headeren (Kartverket via origo-components), samme
    // mønster som drømmeplan-admin. Treff zoomer kartet og markeres med pin.
    const { input, suggestions, isFetchingDetails, handleInputChange, handleSuggestionClick } =
        useAddressSearch({});

    const onSuggestionClick = useCallback((suggestion: AddressSuggestion) => {
        handleSuggestionClick(suggestion);
        mapView?.goTo({
            target: new Point({ x: suggestion.x, y: suggestion.y, spatialReference: { wkid: suggestion.wkid } }),
            zoom: 16,
        });
    }, [handleSuggestionClick, mapView]);

    const openChangelog = () => {
        setChangelogOpen(true);
        dismiss();
    };

    return (
        <AppShell
            className={styles.appShell}
            header={
                <AppHeader
                    logo={<img src={geodataLogo} alt="Geodata" width={34} height={32} />}
                    title={t('appTitle')}
                    subtitle={hideSubtitle ? undefined : t('appSubtitle')}
                    center={hideSearch ? undefined : (
                        <AddressSearch
                            input={input}
                            suggestions={suggestions}
                            isFetchingDetails={isFetchingDetails}
                            onInputChange={handleInputChange}
                            onSuggestionClick={onSuggestionClick}
                            onHoverChange={setHoveredIndex}
                            className={styles.headerSearch}
                        />
                    )}
                    user={!hideUser && user?.fullName ? {
                        name: user.fullName,
                        initials: getInitials(user.fullName),
                        avatarUrl: user.thumbnail,
                    } : undefined}
                    actions={
                        <>
                            <VersionTrigger placement="header" onClick={openChangelog} />
                            {onSignOut && (
                                <Button variant="ghost" size="sm" onClick={() => unsaved.guard(onSignOut)}>
                                    {t('common.logout')}
                                </Button>
                            )}
                        </>
                    }
                />
            }
        >
            <div className={styles.mainColumn}>
                {hasNewVersion && (
                    <NewVersionNotice onOpenChangelog={openChangelog} onDismiss={dismiss} />
                )}
                <div className={styles.body}>
                    <ShellPanel
                        resizable
                        defaultWidth={initialPanelWidth}
                        defaultWidthVw={PANEL_DEFAULT_WIDTH_VW}
                        minWidth={PANEL_MIN_WIDTH}
                        maxWidthVw={PANEL_MAX_WIDTH_VW}
                    >
                        <PlanregisterView unsaved={unsaved} />
                    </ShellPanel>
                    <div className={styles.mapArea}>
                        <div className={styles.mapContainer}>
                            <Map
                                webMapId={webMapId}
                                tools2D={['fullscreen', 'scalebar']}
                                disablePopup
                                disableClick={false}
                            />
                            <SearchSuggestionLayer
                                suggestions={suggestions}
                                hoveredIndex={hoveredIndex}
                                onPinClick={onSuggestionClick}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <ChangelogDialog
                open={changelogOpen}
                onClose={() => setChangelogOpen(false)}
                appName="Planregister-redaktøren"
                changelogMarkdown={changelogMarkdown}
            />
        </AppShell>
    );
};

export default AppLayout;
